/**
 * voiceTelemetry — ce que l'enregistreur et sa file d'envoi disent à la télémétrie, derrière une
 * petite interface locale.
 *
 * Le moteur trace chaque transition (un fil d'Ariane) et chaque échec (une erreur typée, avec le fil
 * des dernières transitions qui l'ont précédé). Où ça part est un branchement, posé une fois au
 * démarrage par `setVoiceTelemetrySink` : le helper commun du moteur (`engine/shared/telemetry`) le
 * fera. Sans branchement, les fils d'Ariane ne vont nulle part et les erreurs vont à la console, pour
 * qu'aucun échec ne disparaisse.
 *
 * Jamais d'id ni de nom de personne dans un message ni dans ses métadonnées : l'état, la durée, le
 * type de fichier, le nombre d'essais.
 */

import { type OutboxTelemetry } from "../shared/outbox/outbox"

/** Ce que la voix dit à la télémétrie : la forme de toute file d'envoi. */
export type VoiceTelemetrySink = OutboxTelemetry

const consoleSink: VoiceTelemetrySink = {
    breadcrumb: () => {},
    error: (error, metadata) => console.warn(`[voice] ${error.name}: ${error.message}`, metadata ?? {}),
}

let sink: VoiceTelemetrySink = consoleSink

/** Branche la télémétrie du moteur ; `null` revient au repli (la console). */
export function setVoiceTelemetrySink(next: VoiceTelemetrySink | null): void {
    sink = next ?? consoleSink
}

export const voiceTelemetry: VoiceTelemetrySink = {
    breadcrumb: (message, metadata) => sink.breadcrumb(message, metadata),
    error: (error, metadata) => sink.error(error, metadata),
}

/** Une erreur de l'enregistreur : son genre dit où, le message d'origine dit pourquoi. */
export class VoiceRecorderError extends Error {
    readonly kind: "start_failed" | "stop_failed" | "pause_failed" | "resume_failed"
    constructor(kind: VoiceRecorderError["kind"], cause: unknown) {
        super(cause instanceof Error ? cause.message : String(cause ?? kind))
        this.name = "VoiceRecorderError"
        this.kind = kind
        if (cause instanceof Error && cause.stack) this.stack = cause.stack
    }
}
